/**
 * Building the scope ladder's client column — the pure half of `ChatScopePanel` (§6b).
 *
 * It lives here, not in the SFC, for the same reason `parseStoredScope` and `decideRememberedScope`
 * do: these are the rules that decide WHICH RUNGS EXIST, and a rule that can only be exercised by
 * mounting a component with an i18n plugin and a store behind it is a rule with no test. The panel
 * keeps the rendering and the selection; this file answers what there is to render.
 */
import type { Client } from '../clients/client'

/**
 * The four fields the ladder reads off a case. Generic rather than `CaseWire`, because the store's
 * `Case` ADDS `documentIds` to the wire shape and the panel hands its own rows straight back to
 * `pickCase` — widening them to the wire type here would strip that on the way through.
 */
export interface ScopeLadderCase {
  id: string
  name: string
  client_id: string | null
  client_name?: string | null
}

/** Column-1 key for the leftover bucket of cases that belong to no client. Never a scope. */
export const NO_CLIENT_KEY = '__none__'

export interface ScopeClientGroup<C extends ScopeLadderCase = ScopeLadderCase> {
  key: string
  /** `null` only for the unassigned bucket, which navigates without scoping. */
  id: string | null
  name: string
  cases: C[]
}

/**
 * Column 1: every client the org has, each carrying its own cases.
 *
 * **The clients come from the client list, not from the cases.** This used to group the case list on
 * `client_id` alone, so a client with no cases had no row — deliberately, on the reasoning that
 * scoping to it restricts the agent to an empty document set. That was overruled
 * (AlexAmin/marcus#85): the document browser lists clients from `/api/clients`, so the two surfaces
 * read different sources and disagreed, and a client created minutes earlier was invisible to the
 * chat with nothing on screen to explain it. Scoping holds a conversation in a client's context; an
 * empty document set is a true answer about a new client, not a reason to withhold the rung.
 *
 * A case whose `client_id` is null still falls to the unassigned bucket, and that is NOT the same
 * state as a client with no cases: `cases.client_id` is `ON DELETE set null`, so a case outliving its
 * client is its own thing. A case naming a client the list did not carry keeps its own
 * `client_name`, so no row is ever nameless.
 */
export function buildScopeGroups<C extends ScopeLadderCase>(
  clients: readonly Client[],
  cases: readonly C[],
  noClientLabel: string,
): ScopeClientGroup<C>[] {
  const byClient = new Map<string, ScopeClientGroup<C>>()
  for (const client of clients) {
    byClient.set(client.id, { key: client.id, id: client.id, name: client.name, cases: [] })
  }
  for (const item of cases) {
    const key = item.client_id ?? NO_CLIENT_KEY
    let group = byClient.get(key)
    if (!group) {
      group = { key, id: item.client_id ?? null, name: item.client_name || noClientLabel, cases: [] }
      byClient.set(key, group)
    }
    group.cases.push(item)
  }
  // Unassigned cases sort last — a leftover bucket, not a peer of the real clients.
  return [...byClient.values()].sort((a, b) => {
    if (!a.id) return 1
    if (!b.id) return -1
    return a.name.localeCompare(b.name)
  })
}

/** One row of the flat search list. Either rung can match a query, so either can appear. */
export type ScopeHit<C extends ScopeLadderCase = ScopeLadderCase> =
  | { kind: 'client'; key: string; group: ScopeClientGroup<C> }
  | { kind: 'case'; key: string; item: C }

/**
 * Search replaces the columns with a flat list (Finder-style) rather than filtering them in place.
 *
 * **Clients match too, and sort first** — the shallower rung, and there are fewer of them. Only cases
 * were matched before, so typing a client's name returned that client's CASES and never the client
 * itself, and a client with no cases matched nothing at all: column 1's missing-client bug wearing
 * the search box instead. The unassigned bucket is deliberately unmatchable — it is a navigation
 * target with no id to scope to, so returning it from a search would offer a row that does nothing.
 */
export function searchScopeLadder<C extends ScopeLadderCase>(
  groups: readonly ScopeClientGroup<C>[],
  cases: readonly C[],
  query: string,
  limit = 50,
): ScopeHit<C>[] {
  const q = query.trim().toLowerCase()
  if (!q) return []
  const hits: ScopeHit<C>[] = groups
    .filter(g => !!g.id && g.name.toLowerCase().includes(q))
    .map(g => ({ kind: 'client' as const, key: `client:${g.key}`, group: g }))
  for (const item of cases) {
    if (item.name.toLowerCase().includes(q) || (item.client_name?.toLowerCase().includes(q) ?? false)) {
      hits.push({ kind: 'case', key: `case:${item.id}`, item })
    }
  }
  return hits.slice(0, limit)
}

/**
 * Every id a remembered scope may legitimately point at — the allow-list `decideRememberedScope`
 * checks before restoring, published by `useRestoreScope`.
 *
 * **The client ids come from the CLIENT LIST.** They used to be collected as the `client_id`s
 * carried by the cases, so a remembered rung pointing at a client with no cases was missing from the
 * allow-list, read as unrecognised, and FORGOTTEN on every page load — the picker's missing-client
 * bug one layer down, and the one that presented as "the setting does not persist"
 * (AlexAmin/marcus#85). A case's own `client_id` is still added: a case can name a client this list
 * did not carry, and an id present twice costs nothing in a Set.
 *
 * Only ever called with lists from a load that is KNOWN to have succeeded — a failed fetch yields
 * empty ones, and an empty allow-list is licence to forget every remembered scope there is.
 */
export function scopeAllowList(
  clients: readonly Client[],
  cases: readonly ScopeLadderCase[],
): Set<string> {
  const ids = new Set<string>()
  for (const client of clients) ids.add(client.id)
  for (const item of cases) {
    ids.add(item.id)
    if (item.client_id) ids.add(item.client_id)
  }
  return ids
}
